import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CalendarSlideshow = ({ showControls = true }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  const slides = [
    { path: '/images/calendar/1a.jpg' },
    { path: '/images/calendar/1b.jpg' },
    { path: '/images/calendar/2.jpg' },
    { path: '/images/calendar/3.jpg' },
    { path: '/images/calendar/4.jpg' },
    { path: '/images/calendar/5.jpg' }
  ];

  const changeSlide = (direction) => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentSlide((prev) => {
        if (direction === 'next') {
          return prev === slides.length - 1 ? 0 : prev + 1;
        }
        return prev === 0 ? slides.length - 1 : prev - 1;
      });
      setTimeout(() => setIsTransitioning(false), 300);
    }
  };

  const nextSlide = () => changeSlide('next');
  const prevSlide = () => changeSlide('prev');

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  useEffect(() => {
    if (!isHovered && showControls) {
      const timer = setInterval(nextSlide, 4000);
      return () => clearInterval(timer);
    }
  }, [isHovered, currentSlide, showControls]);

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      {/* Main Slideshow */}
      <div 
        className="relative w-full rounded-xl md:rounded-2xl overflow-hidden bg-gray-900"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 w-full h-0.5 md:h-1 bg-gray-800 z-10">
          <div 
            className="h-full bg-white/20 transition-all duration-300"
            style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
          />
        </div>

        {/* Image Container */}
        <div className="relative w-full flex items-center justify-center p-3 md:p-8">
          <div className="relative max-w-full max-h-[400px] md:max-h-[600px]">
            <img 
              src={slides[currentSlide].path}
              alt={`Calendar preview ${currentSlide + 1}`}
              className={`w-auto h-auto max-w-full max-h-[400px] md:max-h-[600px] transition-all duration-500 ${
                isTransitioning ? 'scale-105 opacity-80' : 'scale-100 opacity-100'
              }`}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/30 pointer-events-none" />
          </div>

          {/* Navigation Controls */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-2 md:px-4">
            <button 
              onClick={prevSlide}
              className="p-2 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </button>
            <button 
              onClick={nextSlide}
              className="p-2 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Thumbnail Navigation */}
      <div className="overflow-hidden px-1">
        <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 md:pb-4 scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300">
          {slides.map((slide, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className="relative flex-shrink-0 focus:outline-none group"
              aria-label={`View slide ${index + 1}`}
            >
              <div className={`
                relative w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden
                transition-all duration-300 ease-out
                ${currentSlide === index 
                  ? 'ring-2 ring-white shadow-lg transform scale-105' 
                  : 'hover:shadow-md hover:scale-102'
                }
              `}>
                <img 
                  src={slide.path}
                  alt={`Thumbnail ${index + 1}`}
                  className={`
                    w-full h-full object-cover
                    transition-all duration-300
                    ${currentSlide === index 
                      ? 'opacity-100' 
                      : 'opacity-50 group-hover:opacity-75'
                    }
                  `}
                />
                <div className={`
                  absolute inset-0 bg-black/10 transition-opacity duration-300
                  ${currentSlide === index ? 'opacity-0' : 'group-hover:opacity-0'}
                `} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarSlideshow;